import React from "react";
import { useDispatch } from "react-redux";
import { CheckMark, OnboardingHeader } from "../../components/onboarding/OnboardingBrand";
import { TwButton, TwInputGroup, TwMainLayout } from "../../components/tailwind";
import { classNames, isValidEmail } from "../../helpers";
import * as userActions from "../../redux/reducers/userReducer";
import axiosInstance from "../../utils/axiosInstance";
import { getUrlForBookings, getUrlForUsers } from "../../utils/confighelpers";
import { hasClubDetails, isSubscribedOwner, markDashboardTourPending, ONBOARDING_ROUTES, OWNER_BUSINESS_TYPE } from "../../utils/onboarding";

/** Countries where clubs can receive payouts through Stripe Connect Express */
const PAYOUT_COUNTRIES = [
  { iso: "NL", name: "Netherlands" },
  { iso: "BE", name: "Belgium" },
  { iso: "DE", name: "Germany" },
  { iso: "LU", name: "Luxembourg" },
  { iso: "FR", name: "France" },
];

const benefits = [
  { title: "Money straight to your bank", text: "Bookings, passes and subscriptions are paid out to your own account." },
  { title: "iDEAL, cards & Apple Pay", text: "Members pay the way they like — online or in the app." },
  { title: "Safe and verified by Stripe", text: "Stripe checks your details once. It takes about 5 minutes." },
];

export default function Payouts({ navigation }) {
  const dispatch = useDispatch();
  const [loading, setLoading] = React.useState(true);
  const [status, setStatus] = React.useState("NOT_STARTED"); // NOT_STARTED | INITIATED | SUCCESS
  const [email, setEmail] = React.useState("");
  const [country, setCountry] = React.useState("NL");
  const [errors, setErrors] = React.useState({});
  const [redirecting, setRedirecting] = React.useState(false);

  React.useEffect(() => {
    (async () => {
      try {
        const response = await axiosInstance({ method: "get", url: `${getUrlForUsers()}getUserInfo` });
        if (response?.data?.statusCode !== 200) return;
        const data = response.data.data;
        dispatch(userActions.setUserData(data));
        dispatch(userActions.setBusinessType(OWNER_BUSINESS_TYPE));
        if (!isSubscribedOwner(data)) {
          navigation(ONBOARDING_ROUTES.plan, { replace: true });
          return;
        }
        if (!hasClubDetails(data)) {
          navigation(ONBOARDING_ROUTES.club, { replace: true });
          return;
        }
        const stripe = data?.myConnectedPaymentProfiles?.stripe || {};
        const details = stripe.profileDetails || {};
        setStatus(details.stripeOnBoardindStatus === "SUCCESS" ? "SUCCESS" : details.stripeAcId ? "INITIATED" : "NOT_STARTED");
        setEmail(details.stripeEmail || data?.myBusinessProfile?.workEmail || data.email || "");
        setCountry(details.stripeCountry || "NL");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connect = async () => {
    const found = {};
    if (!email || !isValidEmail(email.trim())) found.email = "Please enter a valid email address.";
    setErrors(found);
    if (Object.keys(found).length) return;
    setRedirecting(true);
    try {
      const response = await axiosInstance({
        method: "post",
        url: `${getUrlForBookings()}payments/createConnectedAC`,
        data: { email: email.trim(), country, return_url: `${window.location.origin}${ONBOARDING_ROUTES.payouts}` },
      });
      const url = response?.data?.data?.on_boarding_url;
      if (url) {
        window.location = url;
        return;
      }
      if (response?.data?.statusCode === 200) {
        setStatus("SUCCESS");
      } else {
        setErrors({ email: response?.data?.message || "We couldn't start the Stripe setup. Please check your details." });
      }
    } catch (e) {
      setErrors({ email: "We couldn't reach Stripe. Please try again." });
    }
    setRedirecting(false);
  };

  const goToDashboard = () => {
    markDashboardTourPending();
    navigation("/home/dashboard", { replace: true });
  };

  return (
    <TwMainLayout fullScreen={true}>
      <div className="min-h-screen bg-[#F7F4F8]">
        <OnboardingHeader current={4} />

        <main className="mx-auto max-w-5xl px-4 pb-16 pt-6 sm:px-8 lg:pt-12">
          {loading ? (
            <div className="h-80 animate-pulse rounded-3xl bg-white" />
          ) : status === "SUCCESS" ? (
            <section className="mx-auto max-w-xl rounded-3xl border border-gray-200 bg-white p-6 sm:p-10 text-center shadow-[0_20px_50px_rgba(72,27,58,0.08)]" role="status">
              <div className="fc-pop mx-auto flex h-16 w-16 items-center justify-center rounded-3xl bg-bz-status-green text-white">
                <CheckMark className="h-9 w-9" />
              </div>
              <h1 className="mt-5 text-2xl sm:text-3xl font-black text-gray-900">Payouts are connected</h1>
              <p className="mt-2 text-gray-600">Members can now pay online with iDEAL. You can turn on more payment methods anytime from your account settings.</p>
              <button type="button" onClick={goToDashboard} className="fc-pulse mt-8 w-full rounded-2xl bg-brown-coffee py-3.5 font-extrabold text-white hover:bg-light-brown-coffee">
                Go to my dashboard
              </button>
            </section>
          ) : (
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_420px] lg:gap-8">
              <section>
                <span className="inline-flex items-center rounded-full bg-[#F1EEF2] px-2.5 py-1 text-xs font-extrabold text-gray-600">Optional · you can do this later</span>
                <h1 className="mt-3 text-3xl sm:text-4xl font-black tracking-tight text-gray-900">
                  {status === "INITIATED" ? "Almost there — finish your payout setup" : "Get paid for your classes"}
                </h1>
                <p className="mt-3 text-base sm:text-lg text-gray-600">
                  {status === "INITIATED"
                    ? "Stripe still needs a few details before payouts can start. Pick up where you left off."
                    : "Connect a bank account through Stripe so members can pay for classes, passes and subscriptions online."}
                </p>
                <ul className="mt-6 flex flex-col gap-4">
                  {benefits.map((b) => (
                    <li key={b.title} className="flex gap-3">
                      <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#E8F7EE] text-bz-status-green"><CheckMark className="h-4 w-4" /></span>
                      <span>
                        <span className="block font-extrabold text-gray-900">{b.title}</span>
                        <span className="block text-sm text-gray-600">{b.text}</span>
                      </span>
                    </li>
                  ))}
                </ul>
              </section>

              <aside className="rounded-3xl border border-gray-200 bg-white p-5 sm:p-7 shadow-[0_20px_50px_rgba(72,27,58,0.10)]">
                <div className="flex flex-col gap-5">
                  <TwInputGroup
                    label="Email for your Stripe account"
                    name="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    errors={errors}
                  />
                  <fieldset>
                    <legend className="block text-sm/6 font-medium text-black/[0.6]">Country of your bank account</legend>
                    <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-3">
                      {PAYOUT_COUNTRIES.map((c) => (
                        <button
                          key={c.iso}
                          type="button"
                          onClick={() => setCountry(c.iso)}
                          aria-pressed={country === c.iso}
                          className={classNames(
                            "rounded-xl border-[1.5px] px-3 py-2.5 text-sm font-bold transition",
                            country === c.iso ? "border-indigo-bz bg-[#FBF7FF] text-[#4B0084]" : "border-gray-200 text-gray-700 hover:border-indigo-bz/50"
                          )}
                        >
                          {c.name}
                        </button>
                      ))}
                    </div>
                  </fieldset>
                  <TwButton
                    onClick={connect}
                    disabled={redirecting}
                    actionLoading={redirecting}
                    className="w-full !rounded-2xl !bg-brown-coffee py-3.5 text-base font-extrabold hover:!bg-light-brown-coffee"
                  >
                    {status === "INITIATED" ? "Continue on Stripe" : "Connect with Stripe"}
                  </TwButton>
                  <p className="text-center text-xs text-gray-500">You'll be taken to Stripe and brought back here when you're done.</p>
                  <div className="border-t border-gray-100 pt-4 text-center">
                    <button type="button" onClick={goToDashboard} className="text-sm font-extrabold text-gray-700 underline hover:text-gray-900">
                      Set up later
                    </button>
                    <p className="mt-1 text-xs text-gray-500">You can add classes and invite members now, and connect payouts anytime from your account settings.</p>
                  </div>
                </div>
              </aside>
            </div>
          )}
        </main>
      </div>
    </TwMainLayout>
  );
}
